import { createTheme, type CSSObject } from "@mui/material/styles";
// Side-effect (type-only) import that pulls in the MUI X pickers module
// augmentation, so the MuiPickersOutlinedInput / MuiPickersInputBase component
// keys below are recognized on the theme's `components` map.
import type {} from "@mui/x-date-pickers/themeAugmentation";

// The input look is defined ONCE as CSS variables in index.css (:root, the
// --input-* tokens). Both `.inputDefault` (native inputs) and the MUI overrides
// below (TextFields + the X v9 date/time pickers) consume those same tokens, so
// there is a single source of truth — native inputs and pickers cannot drift.
const V = (name: string): string => `var(--input-${name})`;

// Shared outlined-input styling. MUI's plain inputs and the X pickers are
// structurally identical but use different class prefixes (MuiOutlinedInput vs
// MuiPickersOutlinedInput) for the root, notched outline, and inner input — so
// we parameterize on those names and reuse the exact same rules for both.
const outlinedRoot = (outline: string, input: string): CSSObject => ({
  boxSizing: "border-box",
  minHeight: V("min-height"),
  borderRadius: V("radius"),
  backgroundColor: V("bg"),
  fontSize: V("font"),
  color: V("fg"),
  transition: "background-color .15s, border-color .15s, box-shadow .15s",
  [`& .${outline}`]: { borderColor: V("border") },
  "&:hover": { backgroundColor: V("bg-active") },
  [`&:hover .${outline}`]: { borderColor: V("border-hover") },
  "&.Mui-focused": { backgroundColor: V("bg-active"), boxShadow: V("ring") },
  [`&.Mui-focused .${outline}`]: {
    borderColor: V("border-focus"),
    borderWidth: 1,
  },
  [`&.Mui-error .${outline}`]: { borderColor: V("border-error") },
  "&.Mui-error.Mui-focused": { boxShadow: V("ring-error") },
  [`&.Mui-error.Mui-focused .${outline}`]: {
    borderColor: V("border-error"),
    borderWidth: 1,
  },
  "&.Mui-disabled": {
    backgroundColor: V("bg-disabled"),
    cursor: "not-allowed",
    boxShadow: "none",
  },
  // MUI fades the outline to rgba(0,0,0,.26) when disabled (higher specificity
  // than the resting rule above) — pin it back to the shared border token so a
  // disabled picker keeps the same visible outline as a disabled native input.
  [`&.Mui-disabled .${outline}`]: { borderColor: V("border") },
  [`&.Mui-disabled .${input}`]: {
    cursor: "not-allowed",
    color: V("fg-disabled"),
    WebkitTextFillColor: V("fg-disabled"),
  },
  // Read-only = locked until "Bearbeiten" is on. Same look as disabled, but the
  // text stays selectable for copying, so: the text cursor instead of
  // not-allowed, and no hover/focus highlight (that would read as editable).
  "&.Mui-readOnly, &.Mui-readOnly:hover, &.Mui-readOnly.Mui-focused": {
    backgroundColor: V("bg-disabled"),
    boxShadow: "none",
  },
  [`&.Mui-readOnly .${outline}, &.Mui-readOnly:hover .${outline}, &.Mui-readOnly.Mui-focused .${outline}`]:
    { borderColor: V("border") },
  [`&.Mui-readOnly .${input}`]: {
    color: V("fg-disabled"),
    WebkitTextFillColor: V("fg-disabled"),
  },
});

const pad = `${"var(--input-pad-y)"} ${"var(--input-pad-x)"}`;

export const appTheme = createTheme({
  palette: { primary: { main: "#2989D8" } },
  shape: { borderRadius: 8 },
  typography: { fontSize: 13 },
  components: {
    // Default every MUI text field / picker to the small, outlined look.
    MuiTextField: { defaultProps: { variant: "outlined", size: "small" } },
    // Plain MUI inputs (TextField, Select).
    MuiOutlinedInput: {
      styleOverrides: {
        root: outlinedRoot("MuiOutlinedInput-notchedOutline", "MuiOutlinedInput-input"),
        input: { padding: pad },
      },
    },
    // MUI X v9 date/time pickers render their OWN input components, not
    // MuiOutlinedInput — so they need the identical rules, keyed on their own
    // class names, to match the native inputs.
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: outlinedRoot("MuiPickersOutlinedInput-notchedOutline", "MuiPickersSectionList-root"),
      },
    },
    MuiPickersInputBase: {
      styleOverrides: {
        // line-height matches @tailwindcss/forms' 1.5rem on native inputs so a
        // picker is the exact same height (42px) as a native .inputDefault.
        sectionsContainer: { padding: pad, lineHeight: "1.5rem" },
        root: {
          "&.Mui-disabled": {
            cursor: "not-allowed",
            color: V("fg-disabled"),
            WebkitTextFillColor: V("fg-disabled"),
          },
        },
      },
    },
    MuiButtonBase: { defaultProps: { disableRipple: false } },
  },
});

export default appTheme;
